import { useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';

import 'react-datepicker/dist/react-datepicker.css';

import { BilingualField } from '../../components/BilingualField';
import { DescriptionEditor } from '../../components/DescriptionEditor';
import {
  bilingualPayload,
  emptyBilingual,
  hasBilingualValue,
  toBilingualValue,
  type BilingualValue,
} from '../../components/bilingualValue';
import { HostTourGuideButton, TOUR, tourAttr, useHostTourStore } from '../../features/hostTour';
import { useAuth } from '../../hooks/useAuth';
import { localize } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';
import { passApi, type AdminPassListItem } from '../../services/passApi';

interface ActivityOption {
  id: string;
  titleCN: string;
  titleEN: string | null;
  slug: string;
  hostId: string;
  sessions: Array<{
    id: string;
    startTime: string;
    endTime: string;
    maxCapacity: number;
    currentBookings: number;
  }>;
}

interface PassFormData {
  activityId: string;
  hostId: string;
  title: BilingualValue;
  description: BilingualValue;
  price: string; // dollars (string for input)
  totalSessions: number;
  expiresAt: Date | null;
  maxSales: number;
  sessionIds: string[];
}

export default function AdminPassForm() {
  const siteLanguage = useSiteLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading, getToken } = useAuth();
  const isEditing = !!id;

  // Detect host vs admin context from the current route
  const isHostContext = location.pathname.startsWith('/host/');
  const apiPrefix = isHostContext ? 'host' : 'admin';
  const listPath = isHostContext ? '/host/passes' : '/admin/passes';

  const [loading, setLoading] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activities, setActivities] = useState<ActivityOption[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [activitySessions, setActivitySessions] = useState<ActivityOption['sessions']>([]);

  // For edit mode: track original pass data to diff sessions
  const [originalPass, setOriginalPass] = useState<AdminPassListItem | null>(null);
  const [originalExpiresAt, setOriginalExpiresAt] = useState<Date | null>(null);

  const [formData, setFormData] = useState<PassFormData>({
    activityId: '',
    hostId: '',
    title: emptyBilingual(),
    description: emptyBilingual(),
    price: '',
    totalSessions: 5,
    expiresAt: null,
    maxSales: 10,
    sessionIds: [],
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch activities list
  useEffect(() => {
    const fetchActivities = async () => {
      if (!isAuthenticated) return;
      try {
        setLoadingActivities(true);
        const token = await getToken();
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
        const response = await fetch(`${apiUrl}/${apiPrefix}/activities`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch activities');
        const result = await response.json();
        if (result.success) {
          setActivities(result.data || []);
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load activities';
        setError(errorMessage);
      } finally {
        setLoadingActivities(false);
      }
    };
    fetchActivities();
  }, [isAuthenticated, getToken, apiPrefix]);

  // Fetch pass data if editing
  useEffect(() => {
    if (!isEditing || !isAuthenticated) return;

    const fetchPass = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        if (!token) throw new Error('No access token');
        const data = await passApi.getAdminPasses(token);
        const pass = data.find((p) => p.id === id);
        if (!pass) throw new Error('Pass not found');

        setOriginalPass(pass);
        const expiresDate = new Date(pass.expiresAt);
        setOriginalExpiresAt(expiresDate);

        setFormData({
          activityId: pass.activity.id,
          hostId: pass.host?.id || '',
          title: toBilingualValue({
            zh: pass.titleCN,
            en: pass.titleEN,
            autoLang: pass.titleAutoLang,
          }),
          description: toBilingualValue({
            zh: pass.descriptionCN,
            en: pass.descriptionEN,
            autoLang: pass.descriptionAutoLang,
          }),
          price: String(pass.price / 100),
          totalSessions: pass.totalSessions,
          expiresAt: expiresDate,
          maxSales: pass.maxSales,
          sessionIds: pass.sessions.map((ps) => ps.session.id),
        });
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load pass';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchPass();
  }, [isEditing, id, isAuthenticated, getToken]);

  // Load sessions when activity is selected
  useEffect(() => {
    if (!formData.activityId || !isAuthenticated) {
      setActivitySessions([]);
      return;
    }

    const fetchActivitySessions = async () => {
      try {
        setLoadingSessions(true);
        const token = await getToken();
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
        const response = await fetch(`${apiUrl}/${apiPrefix}/activities/${formData.activityId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch activity details');
        const result = await response.json();
        if (result.success && result.data) {
          const sessions = result.data.sessions || [];
          setActivitySessions(sessions);

          // Resolve hostId from activity
          if (result.data.hostId) {
            setFormData((prev) => ({ ...prev, hostId: result.data.hostId }));
          }
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load sessions';
        setError(errorMessage);
      } finally {
        setLoadingSessions(false);
      }
    };

    fetchActivitySessions();
  }, [formData.activityId, isAuthenticated, getToken, apiPrefix]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value,
    }));
  };

  const handleActivityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const activityId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      activityId,
      sessionIds: [], // Clear session selection when activity changes
    }));
  };

  const handleSessionToggle = (sessionId: string) => {
    setFormData((prev) => ({
      ...prev,
      sessionIds: prev.sessionIds.includes(sessionId)
        ? prev.sessionIds.filter((id) => id !== sessionId)
        : [...prev.sessionIds, sessionId],
    }));
  };

  const handleSelectAllSessions = () => {
    if (formData.sessionIds.length === activitySessions.length) {
      setFormData((prev) => ({ ...prev, sessionIds: [] }));
    } else {
      setFormData((prev) => ({
        ...prev,
        sessionIds: activitySessions.map((s) => s.id),
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    if (!formData.activityId) {
      setError('Please select an activity');
      return;
    }
    if (!hasBilingualValue(formData.title)) {
      setError('Enter a title in Chinese or English');
      return;
    }
    if (!formData.price || Number(formData.price) <= 0) {
      setError('Price must be greater than 0');
      return;
    }
    if (!formData.expiresAt) {
      setError('Expiration date is required');
      return;
    }
    if (formData.sessionIds.length === 0) {
      setError('Please select at least one session');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const token = await getToken();
      if (!token) throw new Error('No access token');

      const priceInCents = Math.round(Number(formData.price) * 100);

      // Convert selected date to PT 23:59:59 (end of day in Pacific Time)
      // Per docs: "expiresAt 日期当天 23:59:59 Pacific Time 截止"
      // Use Intl to get the correct UTC offset (handles DST automatically)
      const selectedDate = formData.expiresAt!;
      const ptDateStr = selectedDate.toLocaleDateString('en-CA', {
        timeZone: 'America/Los_Angeles',
      }); // YYYY-MM-DD
      const ptOffsetParts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
        timeZoneName: 'shortOffset',
      }).formatToParts(selectedDate);
      const offsetStr = ptOffsetParts.find((p) => p.type === 'timeZoneName')?.value || 'GMT-8';
      // Convert "GMT-8" or "GMT-7" to "-08:00" or "-07:00"
      const offsetMatch = offsetStr.match(/GMT([+-]\d+)/);
      const offsetHours = offsetMatch ? parseInt(offsetMatch[1], 10) : -8;
      const tzSign = offsetHours >= 0 ? '+' : '-';
      const tzOffset = `${tzSign}${String(Math.abs(offsetHours)).padStart(2, '0')}:00`;
      const expiresAtISO = new Date(`${ptDateStr}T23:59:59${tzOffset}`).toISOString();

      if (isEditing && originalPass) {
        // Session changes are more likely to fail (business rule checks),
        // so execute them first to avoid partial updates
        const originalSessionIds = new Set(originalPass.sessions.map((ps) => ps.session.id));
        const newSessionIds = new Set(formData.sessionIds);

        const toAdd = formData.sessionIds.filter((sid) => !originalSessionIds.has(sid));
        const toRemove = originalPass.sessions
          .map((ps) => ps.session.id)
          .filter((sid) => !newSessionIds.has(sid));

        const sessionErrors: string[] = [];

        if (toAdd.length > 0) {
          await passApi.addPassSessions(originalPass.id, toAdd, token);
        }

        for (const sessionId of toRemove) {
          try {
            await passApi.removePassSession(originalPass.id, sessionId, token);
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Failed to remove session';
            sessionErrors.push(msg);
          }
        }

        // Update pass basic info
        await passApi.updatePass(
          originalPass.id,
          {
            ...bilingualPayload(formData.title, 'title'),
            ...bilingualPayload(formData.description, 'description'),
            price: priceInCents,
            totalSessions: formData.totalSessions,
            maxSales: formData.maxSales,
            expiresAt: expiresAtISO,
          },
          token
        );

        if (sessionErrors.length > 0) {
          setError(
            `Pass updated, but some sessions could not be removed: ${sessionErrors.join('; ')}`
          );
          return; // Stay on page so admin can see the warning
        }
      } else {
        // Create new pass
        const created = await passApi.createPass(
          {
            activityId: formData.activityId,
            hostId: formData.hostId,
            ...bilingualPayload(formData.title, 'title'),
            ...bilingualPayload(formData.description, 'description'),
            price: priceInCents,
            totalSessions: formData.totalSessions,
            expiresAt: expiresAtISO,
            maxSales: formData.maxSales,
            sessionIds: formData.sessionIds,
          },
          token
        );
        // Let the walkthrough follow up on the list page after a real submit.
        if (isHostContext) {
          useHostTourStore
            .getState()
            .markFormSubmitted('passes', created.status === 'ACTIVE' ? 'live' : 'pending');
        }
      }

      navigate(listPath);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save pass';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const formatSessionDateTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const date = start.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const formatTime = (d: Date) => {
      const h = d.getHours();
      const m = d.getMinutes();
      const period = h >= 12 ? 'pm' : 'am';
      const hour = h % 12 || 12;
      return m === 0 ? `${hour}${period}` : `${hour}:${m.toString().padStart(2, '0')}${period}`;
    };
    return `${date} ${formatTime(start)}-${formatTime(end)}`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link to={listPath} className="text-gray-500 hover:text-gray-700">
              &larr; Back
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Pass' : 'Create Pass'}
            </h1>
            {isHostContext && (
              <div className="ml-auto">
                <HostTourGuideButton />
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline">
              Dismiss
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Activity Selection */}
          <div className="bg-white rounded-lg shadow-sm p-6" {...tourAttr(TOUR.pfActivity)}>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Activity *
              </label>
              {loadingActivities ? (
                <p className="text-sm text-gray-500">Loading activities...</p>
              ) : (
                <select
                  name="activityId"
                  value={formData.activityId}
                  onChange={handleActivityChange}
                  disabled={isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                >
                  <option value="">-- Select an activity --</option>
                  {activities.map((activity) => (
                    <option key={activity.id} value={activity.id}>
                      {localize(activity.titleCN, activity.titleEN, siteLanguage)}
                    </option>
                  ))}
                </select>
              )}
              {isEditing && (
                <p className="text-xs text-gray-500 mt-1">
                  Activity cannot be changed after creation.
                </p>
              )}
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm p-6" {...tourAttr(TOUR.pfBasic)}>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div className="space-y-4">
              <BilingualField
                fieldType="passTitle"
                label="Title"
                required
                value={formData.title}
                onChange={(title) => setFormData((prev) => ({ ...prev, title }))}
                placeholder={{
                  ZH: 'e.g., 2-4岁音乐课 5次通票',
                  EN: 'e.g., Music Class (2-4yo) 5-Session Pass',
                }}
                inputClassName="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
              />

              <DescriptionEditor
                fieldType="passDescription"
                label="Description"
                rows={3}
                value={formData.description}
                onChange={(description) => setFormData((prev) => ({ ...prev, description }))}
                placeholder={{ ZH: '套票说明...', EN: 'Pass description...' }}
                inputClassName="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div {...tourAttr(TOUR.pfPrice)}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (USD) *
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    step="0.01"
                    min="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="125.00"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Total pass price in dollars</p>
                </div>

                <div {...tourAttr(TOUR.pfTotalSessions)}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Sessions *
                  </label>
                  <input
                    type="number"
                    name="totalSessions"
                    value={formData.totalSessions}
                    onChange={handleChange}
                    min={1}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    required
                    disabled={isEditing && (originalPass?.soldCount ?? 0) > 0}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Number of bookable sessions
                    {isEditing && (originalPass?.soldCount ?? 0) > 0 && (
                      <span className="text-amber-600 ml-1">(locked — passes already sold)</span>
                    )}
                  </p>
                </div>

                <div {...tourAttr(TOUR.pfMaxSales)}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Sales *
                  </label>
                  <input
                    type="number"
                    name="maxSales"
                    value={formData.maxSales}
                    onChange={handleChange}
                    min={1}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Max passes to sell</p>
                </div>
              </div>

              <div {...tourAttr(TOUR.pfExpires)}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration Date *
                </label>
                <ReactDatePicker
                  selected={formData.expiresAt}
                  onChange={(date: Date | null) =>
                    setFormData((prev) => ({ ...prev, expiresAt: date }))
                  }
                  minDate={
                    isEditing && originalExpiresAt
                      ? originalExpiresAt
                      : new Date(Date.now() + 24 * 60 * 60 * 1000)
                  }
                  dateFormat="yyyy-MM-dd"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  placeholderText="Select expiration date"
                  required
                />
                {isEditing && (
                  <p className="text-xs text-gray-500 mt-1">
                    Can only be extended (not shortened) to protect existing buyers.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Session Selection */}
          <div className="bg-white rounded-lg shadow-sm p-6" {...tourAttr(TOUR.pfSessions)}>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Available Sessions
              {activitySessions.length > 0 && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({formData.sessionIds.length}/{activitySessions.length} selected)
                </span>
              )}
            </h2>

            {!formData.activityId ? (
              <p className="text-sm text-gray-500">Select an activity first to see sessions.</p>
            ) : loadingSessions ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-sm text-gray-500">Loading sessions...</p>
              </div>
            ) : activitySessions.length === 0 ? (
              <p className="text-sm text-gray-500">
                No sessions found for this activity. Add sessions to the activity first.
              </p>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleSelectAllSessions}
                  className="text-sm text-primary hover:underline mb-3"
                >
                  {formData.sessionIds.length === activitySessions.length
                    ? 'Deselect All'
                    : 'Select All'}
                </button>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {activitySessions.map((session) => (
                    <label
                      key={session.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData.sessionIds.includes(session.id)
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.sessionIds.includes(session.id)}
                        onChange={() => handleSessionToggle(session.id)}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <div className="flex-1">
                        <span className="text-sm text-gray-900">
                          {formatSessionDateTime(session.startTime, session.endTime)}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {session.currentBookings}/{session.maxCapacity} booked
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Submit */}
          <div className="flex items-center gap-4">
            <Link
              to={listPath}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className={`px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors ${
                submitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              {...tourAttr(TOUR.pfSubmit)}
            >
              {submitting ? 'Saving...' : isEditing ? 'Update Pass' : 'Create Pass'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
